import { Grid } from '@mui/material'
import axios from 'api/axios-configure'
import { AnalysisLineChart } from 'components/AnalysisLineChart'
import { LoadingSpinner } from 'components/LoadingSpinner'
import { TableReport } from 'components/TableReport'
import { format } from 'date-fns'
import { Dropdown, Icon, Tooltip } from 'monday-ui-react-core'
import React, { useEffect, useMemo, useRef, useState, useContext } from 'react'
import { Container, Row } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { StatementContext } from 'utils/context/StatementsContext'
import { getRowSelectionBaseKey } from 'utils/commonUtils'
import pdfIcon from './../../assest/images/pdf-icon.svg'
import { Usertype } from '../../utils/Constants'

const PayoutAnalysis = ({type, analysisPayoutFilters, setAnalysisPayoutFilters, setActiveTab,}) => {
    const { getPayoutStatementPDF } = useContext(StatementContext)
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 25, //customize the default page size
    })
    const [sorting, setSorting] = useState([])
    const [rowCount, setRowCount] = useState(0)
    const [tableData, setTableData] = useState([])
    const [hasMassDownload, setHasMassDownload] = useState(type === Usertype.SENDER)
    const [headerData, setHeaderData] = useState([])
    const [chartData, setChartData] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isFetching, setIsFetching] = useState(false)
    const [rowSelection, setRowSelection] = useState({});
    const [analysisPayout, setAnalysisPayout] = useState(null);
    const [selectedStatementType, setSelectedStatementType] = useState({
        value: 1,
        label: 'Accounting Date',
    })
    const [columnVisibility, setColumnVisibility] = useState({
        createdDate: false,
        senderName: type === Usertype.SENDER ? false : true,
        ownerName1: type === Usertype.SENDER ? false : true,
    })

    const isMounted = useRef(false)

    //#endregion

    const setHeaderItems = (data) => {
        return [
            {
                label: 'Payout ID:',
                value: data?.payoutId
                    ? `${data.payoutId} -  ${data.payoutName}`
                    : '-',
            },
            { label: 'Sender:', value: data?.sender ?? '-' },
            {
                label: 'Current Rpt Month:',
                value: data?.currentStmtMonth ?? '-',
            },
            { label: 'Payout Balance:', value: data?.poBalance ?? '-' },
            { label: 'Avg Monthly Change:', value: data?.avgChange ?? '-' },
            { label: 'Est Months To Payout:', value: data?.estPOMonths < 999999 ? data?.estPOMonths : '-' },
            { label: 'Est Payout Date:', value: new Date(data?.estPODate) < new Date(9998, 12, 31) ? data?.estPODate : '-' },
            { label: 'Statement Type:', value: null },
        ]
    }

    const statementTypeOptions = useMemo(
        () => [
            {
                value: 1,
                label: 'Accounting Date',
            },
            {
                value: 2,
                label: 'Activity Date',
            },
        ],
        []
    )

    const handleAnalysisRedirect = (statementType) => {
        const params = {}
        if (Object.keys(analysisPayoutFilters).length) {
            if (!analysisPayout)
                setIsLoading(true)
            else
                setIsFetching(true)

            setActiveTab('analysis')
            setHeaderData(setHeaderItems(null))
            setChartData([])

            params.senderNumber = analysisPayoutFilters.senderNumber
            params.runId = analysisPayoutFilters.runId
            params.documentNumber = analysisPayoutFilters.documentNumber
            params.statementType = statementType
            params.pageNumber = pagination.pageIndex + 1
            params.pageSize = pagination.pageSize   
            params.sortDesc = sorting[0]?.desc
            params.sortBy = sorting[0]?.id

            let url = ''
            if (type === 'sender') {
                url = '/GetSenderPayoutAnalysisPaginated'
            }
            else {
                url = '/GetReceiverPayoutAnalysisPaginated'
            }

            axios.post(url, params)
            .then((response) => {
                setAnalysisPayout(response.data)
                if (response.data.hasPDFMassDownload != null) {
                    setHasMassDownload(response.data.hasPDFMassDownload)
                }
            }).catch(() => {
                setIsLoading(false)
                setIsFetching(false)
            }).finally(() => {
                setIsLoading(false)
                setIsFetching(false)
            })
        }
        isMounted.current = true;
    }

    useEffect(() => {
        const statementTypeLabel = analysisPayoutFilters.statementType
        const statementTypeFind =
            statementTypeOptions.find(
                ({ label }) => label === statementTypeLabel
            ) ?? statementTypeOptions[0]
        setSelectedStatementType({ ...statementTypeFind })
    }, [analysisPayoutFilters])

    useEffect(() => {
        setPagination({
            pageIndex: 0,
            pageSize: 25
        })
        handleAnalysisRedirect(selectedStatementType.value);
    }, [selectedStatementType])

    useEffect(() => {
        if (isMounted.current) {
            handleAnalysisRedirect(selectedStatementType.value);
        }
    }, [sorting])

    const onDownloadButtonClicked = (rows, selectedRadio) => {
        const selectedRows = Object.keys(rows)
        const postData = selectedRows.map(row => {
            const splittedKey = getRowSelectionBaseKey(row).split(',')
            return {
                documentSystemNumber: splittedKey[0],
                runId: splittedKey[1],
                ownerName: splittedKey[2] ?? ''
            }
        })
        showDownloadToast("PDF")
        getPayoutStatementPDF(postData, type)        
    }

    const handlePdfIconClick = (row) => {
        showDownloadToast("PDF")
        const { documenT_SYSTEM_NUMBER, runId } = row
        const postData = []
        const data = {
            documentSystemNumber: documenT_SYSTEM_NUMBER,
            ownerName: "",
            runId
        }
        postData.push(data)

        if (postData.length) {
            getPayoutStatementPDF(postData, type)
        }
    }

    const showDownloadToast = (fileType) => {
        toast.success(`Downloading ${fileType}`, {
            position: toast.POSITION.BOTTOM_RIGHT,
            className: 'toastify__toast--success-custom',
            bodyClassName: 'toastify-body',
            progressClassName: 'toastify-progress-bar',
        })
    }

    useEffect(() => {
        setHeaderData(setHeaderItems(null))
        setChartData([])
        if (!analysisPayout) return

        if (analysisPayout?.detail?.items) {
            const filterData = analysisPayout.detail.items.filter((item) => {
                item.delta = formatAmount(item.delta)
                item.endingBalance = formatAmount(item.endingBalance)
                item.totalAdjustment = formatAmount(item.totalAdjustment)
                item.totalRevenue = formatAmount(item.totalRevenue)
                item.totalExpense = formatAmount(item.totalExpense)

                return item
            })
            setTableData(filterData)
            setRowCount(analysisPayout.detail.totalCount)
            setChartData(analysisPayout.chartDetail ?? [])
        } else {
            setTableData([])
            setRowCount(0)
        }

        setHeaderData(setHeaderItems(analysisPayout))
    }, [analysisPayout])

    useEffect(() => {
        if (isMounted.current) {
            handleAnalysisRedirect(selectedStatementType.value)
        }
    }, [pagination.pageIndex, pagination.pageSize])

    useEffect(() => {
        if (isMounted.current) {
            handleAnalysisRedirect(selectedStatementType.value)
        }
    }, [sorting])

    const columns = useMemo(
        () => [
            {
                header: 'Actions',
                size: 60,
                enableSorting: false,
                maxSize: 60,
                Cell: ({ row }) => {
                    return (
                        <div className="d-flex align-items-center">
                            <Tooltip content="Download PDF">
                                <Icon
                                    iconSize={24}
                                    iconType={Icon.type.SRC}
                                    icon={pdfIcon}
                                    iconLabel="pdf icon"
                                    className="icon-story-custom-icon actions-icon"
                                    onClick={() =>
                                        handlePdfIconClick(row.original)
                                    }
                                />
                            </Tooltip>
                        </div>
                    )
                },
            },
            {
                accessorKey: 'reportingMonth',
                size: 80,
                maxSize: 80,
                header: 'Reporting Month',
                Cell: ({ cell }) => {
                    const dateString = cell.getValue()
                    const date = new Date(dateString)
                    const result = format(date, 'MM/dd/yyyy')
                    return <>{result}</>
                },
            },
            {
                accessorKey: 'statementDate',
                size: 80,
                maxSize: 80,
                header: 'Statement Date',
                Cell: ({ cell }) => {
                    const dateString = cell.getValue()
                    const date = new Date(dateString)
                    const result = format(date, 'MM/dd/yyyy')
                    return <>{result}</>
                },
            },
            {
                size: 100,
                maxSize: 100,
                accessorKey: 'status',
                header: 'Statement Status',
            },
            {
                size: 100,
                maxSize: 100,
                accessorKey: 'totalExpense',
                muiTableHeadCellProps: {
                    align: 'right',
                },
                muiTableBodyCellProps: {
                    align: 'right',
                },
                header: 'Total Expenses',
            },
            {
                size: 100,
                maxSize: 100,
                accessorKey: 'totalRevenue',
                muiTableHeadCellProps: {
                    align: 'right',
                },
                muiTableBodyCellProps: {
                    align: 'right',
                },
                header: 'Total Revenue',
            },
            {
                size: 100,
                maxSize: 100,
                accessorKey: 'endingBalance',
                muiTableHeadCellProps: {
                    align: 'right',
                },
                muiTableBodyCellProps: {
                    align: 'right',
                },
                header: 'Payout Balance',
            },
            {
                size: 100,
                maxSize: 100,
                accessorKey: 'delta',
                enableSorting: false,
                muiTableHeadCellProps: {
                    align: 'right',
                },
                muiTableBodyCellProps: {
                    align: 'right',
                },
                header: 'Amount Change',
            },
            {
                size: 100,
                maxSize: 100,
                accessorKey: 'deltaPercentage',
                enableSorting: false,
                muiTableHeadCellProps: {
                    align: 'right',
                },
                muiTableBodyCellProps: {
                    align: 'right',
                },
                header: 'Percent Change',
            },
        ],
        []
    )

    const handleBackButton = () => {
        setActiveTab('all')
    }

    const formatAmount = (amount) => {
        if (!amount) return ''

        if (amount.includes('-')) return '(' + amount.replace('-', '') + ')'

        return amount
    }

    return (
        <>
            <Container
                id="Container"
                style={{
                    border: '1px solid #DDE0ED',
                    borderRadius: '15px',
                    marginTop: '15px',
                    paddingLeft: '15px',
                }}
                className="mt-2 mb-2 py-3"
            >
                <Grid container spacing={4}>
                    <Grid
                        className="d-flex flex-column gap-2"
                        style={{fontSize: 14}}
                        item
                        xs={12}
                        md={4}
                        lg={3}
                    >
                        {headerData.map(({ label, value }, index) => {
                            var labelValue = (
                                <label className="text-left">{value}</label>
                            )
                            if (label.includes('Statement Type'))
                                labelValue = (
                                    <div
                                        style={{
                                            width: '180px',
                                        }}
                                    >
                                        <Dropdown
                                            placeholder="Statement Type"
                                            options={statementTypeOptions}
                                            onChange={setSelectedStatementType}
                                            clearable={false}
                                            className="status-picker w-100"
                                            size={Dropdown.size.SMALL}
                                            value={{ ...selectedStatementType }}
                                        />
                                    </div>
                                )

                            return (
                                <Grid container key={label} spacing={2}>
                                    <Grid item xs={6} className="fw-bold">
                                        <label>{label ?? '-'}</label>
                                    </Grid>
                                    <Grid item xs={6}>
                                        {labelValue}
                                    </Grid>
                                </Grid>
                            )
                        })}
                    </Grid>
                    <Grid item xs={12} md={8} lg={9} style={{ paddingTop: 0 }}>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr)',
                            }}
                        >
                            <AnalysisLineChart
                                dataChart={chartData}
                                showChart={true}
                            />
                        </div>
                    </Grid>
                </Grid>
            </Container>
            <Row>
                <section className="statements-container">
                    <div>
                        <TableReport
                            enableManualSorting={true}
                            onPaginationChange={setPagination}
                            sorting={sorting}
                            pagination={pagination}
                            onSortingChange={setSorting}
                                enableRowSelection={true}
                                hasMassDownload={hasMassDownload}
                            rowCount={rowCount}
                            columns={columns}
                            tableData={tableData}
                            onDownloadButtonClicked={onDownloadButtonClicked}
                            columnVisibility={columnVisibility}
                            setColumnVisibility={setColumnVisibility}
                            isLoading={isLoading}
                            isFetching={isFetching}
                            setRowSelection={setRowSelection}
                            rowSelection={rowSelection}
                            rowId={(row) => { 
                                if(tableData.length > 0)
                                    return (row.documenT_SYSTEM_NUMBER ?? '') + ',' + (row.runId ?? '') + ',' + (row.ownerName1 ?? '') + ',' + (row.hasDownloadFile)
                            }}
                            isPayout={true}
                        />
                    </div>
                </section>
            </Row>
        </>
    )
}
export default PayoutAnalysis
